// Countdown SFX for the 3-2-1-GO sequence — an "arcade" kit: bright triangle pips
// on 3/2/1 and a higher, octave-up GO. Tuned to the gameplay track's A-minor
// palette; the controller routes these past the music fade so they cut through
// the swell. Triangle reads "retro arcade" without the harsh edge of a raw square.

import { getNoiseBuffer, mtof, note } from "./synth";

interface Voice {
  freq: number;
  gain: number;
  type?: OscillatorType;
}

// A small blip: one or more partials sharing a smooth exponential amp envelope.
// Soft attack + exp decay reads as a clean pip, not a harsh beep.
function blip(
  ctx: AudioContext,
  dest: AudioNode,
  voices: Voice[],
  time: number,
  attack: number,
  decay: number,
): void {
  voices.forEach((v) => {
    const osc = ctx.createOscillator();
    osc.type = v.type ?? "triangle";
    osc.frequency.setValueAtTime(v.freq, time);
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(v.gain, time + attack);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + decay);
    osc.connect(amp);
    amp.connect(dest);
    osc.start(time);
    osc.stop(time + decay + 0.05);
  });
}

// A 3/2/1 pip (same pitch for each).
export function playCountdownCount(ctx: AudioContext, dest: AudioNode, time: number): void {
  blip(ctx, dest, [{ freq: mtof(note("A", 5)), gain: 0.34, type: "triangle" }], time, 0.003, 0.12);
}

// The "GO" blip: an octave up, a touch longer, with the lower octave under it for
// body. The A6 (1760Hz) top is also a unique marker the music never produces.
export function playCountdownGo(ctx: AudioContext, dest: AudioNode, time: number): void {
  blip(
    ctx,
    dest,
    [
      { freq: mtof(note("A", 6)), gain: 0.4, type: "triangle" },
      { freq: mtof(note("A", 5)), gain: 0.18, type: "triangle" },
    ],
    time,
    0.003,
    0.3,
  );
}
